(() => {
  'use strict';

  const responsiveSrcset = (source, widths) => {
    try {
      const url = new URL(source, globalThis.location?.origin || 'https://avidrace.com');
      if (!url.pathname.includes('/cdn/shop/')) return '';
      return widths.map((width) => {
        const candidate = new URL(url);
        candidate.searchParams.set('width', String(width));
        return `${candidate.toString()} ${width}w`;
      }).join(', ');
    } catch (_error) {
      return '';
    }
  };

  const QUERY_PREFIX = 'shock_';
  const FILTER_KEYS = [
    'make',
    'model',
    'subModel',
    'years',
    'position',
    'trackWidth',
    'fitmentType',
    'type',
    'body',
    'stroke',
    'collapsed',
    'extended',
    'mountBolt',
    'reservoir',
    'adjustment',
    'bypassTubes',
    'manufacturer',
    'availability'
  ];
  const MODE_FILTERS = {
    vehicle: ['make', 'model', 'subModel', 'years', 'position', 'trackWidth', 'fitmentType'],
    spec: ['type', 'body', 'stroke', 'collapsed', 'extended', 'mountBolt', 'reservoir', 'adjustment', 'bypassTubes'],
    part: []
  };
  const COMMON_FILTERS = ['manufacturer', 'availability'];
  const DYNAMIC_FILTERS = [
    'make',
    'model',
    'subModel',
    'years',
    'position',
    'trackWidth',
    'type',
    'body',
    'stroke',
    'collapsed',
    'extended',
    'mountBolt',
    'reservoir',
    'adjustment',
    'bypassTubes',
    'manufacturer',
    'availability'
  ];
  const FILTER_LABELS = {
    make: 'Make',
    model: 'Model',
    subModel: 'Sub-model',
    years: 'Year',
    position: 'Position',
    trackWidth: 'Track width',
    fitmentType: 'Fitment',
    type: 'Shock type',
    body: 'Body',
    stroke: 'Stroke',
    collapsed: 'Collapsed length',
    extended: 'Extended length',
    mountBolt: 'Mount bolt',
    reservoir: 'Reservoir',
    adjustment: 'Adjustment',
    bypassTubes: 'Bypass tubes',
    manufacturer: 'Manufacturer',
    availability: 'Availability',
    query: 'Part number'
  };
  const OPTION_PLACEHOLDERS = {
    make: 'All makes',
    model: 'All models',
    subModel: 'All sub-models',
    years: 'All available years',
    position: 'Front or rear',
    trackWidth: 'Any available width',
    type: 'All shock types',
    body: 'All body sizes',
    stroke: 'All stroke lengths',
    collapsed: 'All collapsed lengths',
    extended: 'All extended lengths',
    mountBolt: 'All available bolt sizes',
    reservoir: 'All reservoir styles',
    adjustment: 'Any confirmed adjustment',
    bypassTubes: 'Any tube count',
    manufacturer: 'All manufacturers',
    availability: 'All products'
  };
  const TYPE_LABELS = {
    'direct-fit': 'Direct fit',
    coilover: 'Coilover',
    bypass: 'External bypass',
    'internal-bypass': 'Internal bypass / MBR',
    'smooth-body': 'Smooth body',
    'air-shock': 'Air shock',
    'bump-stop': 'Hydraulic bump stop',
    'performance-kit': 'Performance kit',
    'air-strut': 'Air strut',
    shock: 'Shock',
    component: 'Shock component',
    other: 'Other shock product'
  };
  const FITMENT_LABELS = {
    'direct-fit': 'Direct fit',
    'verified-direct-fit': 'Direct fit',
    'conditional-direct-fit': 'Conditional direct fit',
    universal: 'Universal / custom',
    'universal-custom': 'Universal / custom',
    'needs-review': 'Needs review'
  };
  const MODEL_PATTERNS = [
    ['Maverick R', /\bmaverick\s+r\b/i],
    ['Maverick X3', /\b(?:maverick\s+)?x3\b/i],
    ['Maverick', /\bmaverick\b/i],
    ['RZR Pro R', /\brzr\s+pro\s+r\b/i],
    ['RZR Turbo R', /\b(?:rzr\s+)?turbo\s+r\b/i],
    ['RZR XP 1000', /\brzr\s+xp\s*1000\b/i],
    ['RZR', /\brzr\b/i],
    ['Ranger', /\branger\b/i],
    ['YXZ1000R', /\byxz\s*1000r?\b/i],
    ['Talon', /\btalon\b/i],
    ['Pioneer', /\bpioneer\b/i],
    ['Bronco', /\bbronco\b/i],
    ['F-150', /\bf[ -]?150\b/i],
    ['Super Duty', /\bsuper\s+duty\b/i],
    ['Silverado', /\bsilverado\b/i],
    ['Sierra', /\bsierra\b/i],
    ['Colorado', /\bcolorado\b/i],
    ['Tacoma', /\btacoma\b/i],
    ['Tundra', /\btundra\b/i],
    ['4Runner', /\b4runner\b/i],
    ['Wrangler', /\bwrangler\b/i],
    ['Gladiator', /\bgladiator\b/i]
  ];

  const normalizeSpace = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
  const normalizeKey = (value) => normalizeSpace(value).toLowerCase();
  const normalizePart = (value) => String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const isPresent = (value) => value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
  const unique = (values) => [...new Set(values.filter(isPresent))];
  const flattenCatalogPages = (pages, pageResults) => pages.flatMap((page) => pageResults.get(page) || []);
  const humanize = (value) => normalizeSpace(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const formatNumber = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return normalizeSpace(value);
    return Number(number.toFixed(3)).toString();
  };
  const formatInches = (value) => {
    const clean = formatNumber(value);
    if (!clean) return '';
    return /(?:\bin(?:ch(?:es)?)?\b|[\"”])$/i.test(clean) ? clean : `${clean}\u2033`;
  };
  const toArray = (value) => {
    if (!isPresent(value)) return [];
    if (Array.isArray(value)) return unique(value.flatMap(toArray));
    if (typeof value === 'object') {
      if ('value' in value) return toArray(value.value);
      return [];
    }
    const text = normalizeSpace(value);
    if (!text) return [];
    if (text.startsWith('[') && text.endsWith(']')) {
      try {
        return toArray(JSON.parse(text));
      } catch (_error) {
        // Continue with the human-entered delimiter fallback.
      }
    }
    return unique(text.split(/\s*[|;]\s*/).map(normalizeSpace));
  };
  const toFacetArray = (value) => unique(toArray(value).flatMap((item) => (
    normalizeSpace(item).split(/\s*,\s*/).map(normalizeSpace)
  )));
  const parseNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const match = normalizeSpace(value).match(/-?\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : null;
  };
  const parseBoolean = (value) => {
    if (value === true || value === false) return value;
    if (!isPresent(value)) return null;
    const token = normalizeKey(value);
    if (['true', 'yes', '1', 'y'].includes(token)) return true;
    if (['false', 'no', '0', 'n'].includes(token)) return false;
    return null;
  };
  const normalizeVehicleFitments = projection => {
    if (projection?.contractVersion !== 1 || !Array.isArray(projection.rules)) return [];
    const rules=(globalThis.AvidCatalogFitment?.catalogFitmentDiscoveryFacets(projection.rules) || []).map(row=>({
      ...row, modelFamily:row.model, subModel:row.submodel,
      trackWidth:parseNumber(row.configuration?.track_width_in),
    }));
    const pending=projection.reviewRequired===true&&Array.isArray(projection.discoveryModelIds)
      ? (globalThis.AvidVehicleSelector?.directory()||[]).filter(model=>projection.discoveryModelIds.includes(model.modelId)).map(model=>({
        modelId:model.modelId,make:model.make,model:model.model,modelFamily:model.model,
        subModel:'',submodelId:null,years:[],position:'',trackWidth:null,requirements:[],modifications:[],exclusions:[],pendingDiscovery:true,
      })) : [];
    return [...rules,...pending];
  };

  function canonicalType(value, allowCustom = true) {
    const token = normalizeKey(value).replace(/_/g, ' ');
    if (!token) return '';
    if (/internal\s+bypass|\bmbr\b/.test(token)) return 'internal-bypass';
    if (/bump\s*stop/.test(token)) return 'bump-stop';
    if (/air\s*shock/.test(token)) return 'air-shock';
    if (/performance\s+(?:kit|system)|suspension\s+kit|spring\s+kit|shock\s+kit|package|dual\s+shock/.test(token)) return 'performance-kit';
    if (/smooth[ -]?body/.test(token)) return 'smooth-body';
    if (/bypass/.test(token)) return 'bypass';
    if (/coil[ -]?over/.test(token)) return 'coilover';
    if (/component|hardware|bearing|seal|spring|hose|mount|tool/.test(token)) return 'component';
    if (/air\s+strut/.test(token)) return 'air-strut';
    if (/^shock$/.test(token)) return 'shock';
    return allowCustom ? token.replace(/\s+/g, '-') : '';
  }

  function canonicalFitment(value) {
    const token = normalizeKey(value).replace(/[_\s]+/g, '-');
    if (!token) return '';
    if (/verified.*direct|direct.*verified/.test(token)) return 'verified-direct-fit';
    if (/conditional.*direct|direct.*conditional/.test(token)) return 'conditional-direct-fit';
    if (/direct|bolt-on|bolt-on-replacement/.test(token)) return 'direct-fit';
    if (/universal|custom/.test(token)) return 'universal';
    if (/review|unknown|incomplete/.test(token)) return 'needs-review';
    return token;
  }

  function canonicalReservoir(value) {
    const token = normalizeKey(value).replace(/_/g, ' ');
    if (!token) return '';
    if (/piggy\s*back/.test(token)) return 'piggyback';
    if (/remote/.test(token)) return 'remote';
    if (/emulsion|non[ -]?reservoir|no reservoir/.test(token)) return 'emulsion';
    return token.replace(/\s+/g, '-');
  }

  function canonicalDataStatus(value) {
    const token = normalizeKey(value).replace(/[\s-]+/g, '_');
    if (!token) return 'missing';
    if (/(?:^|_)(?:blocked|quarantined|conflict)(?:_|$)/.test(token) || token.includes('review_required') || token.includes('needs_review')) return 'blocked';
    if (token.includes('partial')) return 'partial';
    if (['verified', 'complete', 'reviewed', 'authoritative_reviewed'].includes(token)) return 'verified';
    return 'missing';
  }

  function normalizeProduct(raw, index, reviewMode = false) {
    const title = normalizeSpace(raw.title);
    const searchTitle = `${title} ${toArray(raw.tags).join(' ')}`;
    const inferred = new Set();
    const exact = new Set();
    const markExact = (key, value) => {
      if (isPresent(value)) exact.add(key);
      return value;
    };
    const markInferred = (key, value) => {
      if (isPresent(value) && !(Array.isArray(value) && value.length === 0)) inferred.add(key);
      return value;
    };

    let type = canonicalType(markExact('type', raw.shockType));
    if (!type) type = markInferred('type', canonicalType(searchTitle, false));

    const dimensionMatch = searchTitle.match(/\b(2(?:\.0|\.125|\.25|\.5)?|3(?:\.0)?)\s*(?:[\"”]|in(?:ch(?:es)?)?)?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*(?:[\"”]|in(?:ch(?:es)?)?)?/i);
    const bodyRaw = markExact('body', raw.bodyDiameter);
    const strokeRaw = markExact('stroke', raw.stroke);
    const bodyNumber = parseNumber(isPresent(bodyRaw) ? bodyRaw : dimensionMatch?.[1]);
    const strokeNumber = parseNumber(isPresent(strokeRaw) ? strokeRaw : dimensionMatch?.[2]);
    if (!isPresent(bodyRaw) && bodyNumber !== null) markInferred('body', bodyNumber);
    if (!isPresent(strokeRaw) && strokeNumber !== null) markInferred('stroke', strokeNumber);

    const collapsedRaw = markExact('collapsed', raw.collapsedLength);
    const extendedRaw = markExact('extended', raw.extendedLength);
    const shaftRaw = markExact('shaft', raw.shaftDiameter);
    let shaftNumber = parseNumber(shaftRaw);
    if (shaftNumber === null) {
      const shaftMatch = searchTitle.match(/(?:\bshaft(?:\s+size)?\s*(?:is|:|-)?\s*|\b)(\.?\d+(?:\.\d+)?)\s*(?:[\"”]|in(?:ch(?:es)?)?)?\s+shaft\b/i);
      if (shaftMatch) shaftNumber = parseNumber(markInferred('shaft', shaftMatch[1]));
    }

    const vehicleFitments = normalizeVehicleFitments(raw.catalogFitment);
    const models = vehicleFitments.length ? markExact('model', unique(vehicleFitments.map((fitment) => fitment.model))) : [];
    const makes = vehicleFitments.length ? markExact('make', unique(vehicleFitments.map((fitment) => fitment.make))) : [];
    const years = vehicleFitments.length ? markExact('years', unique(vehicleFitments.flatMap((fitment) => fitment.years))) : [];
    const positions = vehicleFitments.length ? markExact('position', unique(vehicleFitments.map((fitment) => fitment.position))) : [];

    let trackWidth = parseNumber(markExact('trackWidth', raw.trackWidth));
    if (trackWidth === null) {
      const trackMatch = searchTitle.match(/\b(\d+(?:\.\d+)?)\s*(?:[\"”]|in(?:ch(?:es)?)?)\s+(?:track|wide)\b/i);
      if (trackMatch) trackWidth = parseNumber(markInferred('trackWidth', trackMatch[1]));
    }

    const fitmentType=canonicalFitment(raw.fitmentType)==='universal'&&!vehicleFitments.length&&!raw.catalogFitment?.reviewRequired?'universal':'needs-review';
    markExact('fitmentType',fitmentType);

    let reservoir = canonicalReservoir(markExact('reservoir', raw.reservoirType));
    if (!reservoir) {
      const inferredReservoir = /\bpiggy[ -]?back\b/i.test(searchTitle)
        ? 'piggyback'
        : /\bremote(?:\s+reservoir)?\b/i.test(searchTitle)
          ? 'remote'
          : /\bemulsion\b|\bnon[ -]?reservoir\b/i.test(searchTitle)
            ? 'emulsion'
            : '';
      reservoir = markInferred('reservoir', inferredReservoir);
    }

    let compressionAdjustable = parseBoolean(markExact('compressionAdjustable', raw.compressionAdjustable));
    if (compressionAdjustable === null && /\bcompression\s+adjust(?:er|able|ment)\b/i.test(searchTitle)) {
      compressionAdjustable = Boolean(markInferred('compressionAdjustable', true));
    }
    let reboundAdjustable = parseBoolean(markExact('reboundAdjustable', raw.reboundAdjustable));
    if (reboundAdjustable === null && /\brebound\s+adjust(?:er|able|ment)\b/i.test(searchTitle)) {
      reboundAdjustable = Boolean(markInferred('reboundAdjustable', true));
    }

    let bypassTubeCount = parseNumber(markExact('bypassTubes', raw.bypassTubeCount));
    if (bypassTubeCount === null) {
      const tubeMatch = searchTitle.match(/\b(\d+)\s*[- ]?tube\b/i);
      if (tubeMatch) bypassTubeCount = parseNumber(markInferred('bypassTubes', tubeMatch[1]));
    }

    const manufacturer = normalizeSpace(markExact('manufacturer', raw.manufacturer) || raw.vendor || '');
    const manufacturerPartNumber = normalizeSpace(markExact('manufacturerPartNumber', raw.manufacturerPartNumber) || raw.sku || '');
    const criticalExact = ['body', 'stroke', 'collapsed', 'extended'].filter((key) => exact.has(key)).length;
    const dataStatus = canonicalDataStatus(raw.dataStatus);
    const eligibility = parseBoolean(markExact('eligible', raw.eligible));
    const hasExactCoreSpec = criticalExact > 0;
    const price = Number(raw.price) || 0;
    const statusAllowsBrowse = dataStatus === 'verified' || dataStatus === 'partial';
    const hasExactVehicleFitment = vehicleFitments.length > 0;
    const productionBrowseEligible = price > 0 && statusAllowsBrowse && eligibility === true;
    const specBrowseEligible = productionBrowseEligible
      || (price > 0 && reviewMode && dataStatus === 'partial' && hasExactCoreSpec);
    const vehicleBrowseEligible = hasExactVehicleFitment && (
      productionBrowseEligible || (price > 0 && reviewMode && dataStatus === 'partial')
    );
    const browseEligible = specBrowseEligible || vehicleBrowseEligible;

    return {
      id: String(raw.id || raw.handle || index),
      rank: index,
      handle: normalizeSpace(raw.handle),
      title,
      url: normalizeSpace(raw.url),
      vendor: normalizeSpace(raw.vendor),
      manufacturer,
      manufacturerPartNumber,
      sku: normalizeSpace(raw.sku),
      variantId: String(raw.variantId || ''),
      productType: normalizeSpace(raw.productType),
      type,
      fitmentType,
      baseFitmentType: fitmentType,
      catalogFitment: raw.catalogFitment,
      sourceUrl: normalizeSpace(raw.url),
      vehicleFitments,
      makes,
      models,
      years,
      positions,
      bodyNumber,
      shaftNumber,
      strokeNumber,
      collapsedNumber: parseNumber(collapsedRaw),
      extendedNumber: parseNumber(extendedRaw),
      mountingWidth: normalizeSpace(markExact('mountingWidth', raw.mountingWidth)),
      mountBolt: normalizeSpace(markExact('mountBolt', raw.mountingBoltSize)),
      upperMount: normalizeSpace(markExact('upperMount', raw.upperMountType)),
      lowerMount: normalizeSpace(markExact('lowerMount', raw.lowerMountType)),
      reservoir,
      hoseOrientation: normalizeSpace(markExact('hoseOrientation', raw.hoseOrientation)),
      compressionAdjustable,
      reboundAdjustable,
      bypassTubeCount,
      bypassStyle: normalizeSpace(markExact('bypassStyle', raw.bypassStyle)),
      springConfiguration: normalizeSpace(markExact('springConfiguration', raw.springConfiguration)),
      trackWidth,
      application: normalizeSpace(markExact('application', raw.application)),
      fitmentNotes: unique(vehicleFitments.flatMap(row=>[...row.requirements,...row.modifications,...row.exclusions])).join(' · '),
      unitOfSale: normalizeSpace(markExact('unitOfSale', raw.unitOfSale)),
      series: normalizeSpace(markExact('series', raw.series)),
      available: raw.available === true,
      price,
      priceLabel: normalizeSpace(raw.priceLabel),
      compareAtPrice: Number(raw.compareAtPrice) || 0,
      compareAtPriceLabel: normalizeSpace(raw.compareAtPriceLabel),
      image: normalizeSpace(raw.image),
      imageAlt: normalizeSpace(raw.imageAlt || raw.title),
      summary: normalizeSpace(raw.summary),
      tags: toArray(raw.tags),
      dataStatus,
      eligibility,
      browseEligible,
      specBrowseEligible,
      vehicleBrowseEligible,
      hasExactCoreSpec,
      hasExactVehicleFitment,
      inferred,
      exact,
      searchText: normalizeKey([
        title,
        raw.sku,
        manufacturerPartNumber,
        manufacturer,
        raw.series,
        raw.application,

        ...toArray(raw.tags)
      ].filter(Boolean).join(' ')),
      partSearchText: normalizePart([title, raw.sku, manufacturerPartNumber].filter(Boolean).join(' '))
    };
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function resolveShockVehicle(filters, groups, current = null) {
    const selector = globalThis.AvidVehicleSelector;
    const year = Number(filters.years);
    if (!filters.make || !filters.model || !Number.isInteger(year) || year < 1900) return null;
    const candidates = groups.filter(group => group.modelId === filters.model && normalizeKey(group.make) === normalizeKey(filters.make))
      .flatMap(group => group.applications.filter(application => year >= application.yearStart && year <= application.yearEnd
        && (!filters.subModel || application.submodelId === filters.subModel))
        .map(application => selector.resolve({applicationId:application.applicationId,modelId:group.modelId,year},groups))).filter(Boolean);
    // An existing exact configuration can be retained, but never choose between
    // different configurations on behalf of a customer browsing a broad model.
    return candidates.find(candidate => candidate.applicationId === current?.applicationId)
      || (candidates.length === 1 ? candidates[0] : null);
  }

  function contextualFitmentType(product, context) {
    const decision = context && product.catalogFitment?.contractVersion === 1
      ? globalThis.AvidCatalogFitment.evaluateProjectedCatalogFitment(product.catalogFitment.rules,context) : null;
    return decision?.outcome === 'compatible' ? 'verified-direct-fit'
      : decision?.outcome === 'conditional' ? 'conditional-direct-fit'
      : decision?.outcome === 'incompatible' ? 'incompatible'
      : product.baseFitmentType;
  }

  function initShockCenter(root) {
    if (!root || root.dataset.shockCenterReady === 'true') return;
    root.dataset.shockCenterReady = 'true';

    const sectionId = root.dataset.sectionId;
    const pageSize = Math.max(1, Number(root.dataset.pageSize) || 20);
    const sourceProductCount = Math.max(0, Number(root.dataset.sourceCount) || 0);
    const reviewMode = root.dataset.reviewMode === 'true';
    const supportUrl = normalizeSpace(root.dataset.supportUrl) || '/pages/contact';
    const form = root.querySelector('[data-shock-filter-form]');
    const resultsRegion = root.querySelector('[data-shock-results-region]');
    const resultsGrid = root.querySelector('[data-shock-results]');
    const resultCount = root.querySelector('[data-shock-result-count]');
    const dataStatus = root.querySelector('[data-shock-data-status]');
    const chips = root.querySelector('[data-shock-filter-chips]');
    const emptyState = root.querySelector('[data-shock-empty]');
    const catalogGate = root.querySelector('[data-shock-catalog-gate]');
    const loadCatalog = root.querySelector('[data-shock-load-catalog]');
    const loadMore = root.querySelector('[data-shock-load-more]');
    const sortSelect = root.querySelector('[data-shock-sort]');
    const partSearch = root.querySelector('[data-shock-part-search]');
    const compareBar = root.querySelector('[data-shock-compare-bar]');
    const compareCount = root.querySelector('[data-shock-compare-count]');
    const compareDialog = root.querySelector('[data-shock-compare-dialog]');
    const compareContent = root.querySelector('[data-shock-compare-content]');
    const vehicleContextRegion = root.querySelector('[data-shock-vehicle-context]');
    const filterSelects = new Map([...root.querySelectorAll('[data-shock-filter]')].map((select) => [select.dataset.shockFilter, select]));
    const tabs = [...root.querySelectorAll('[data-shock-mode]')];
    const panels = [...root.querySelectorAll('[data-shock-panel]')];
    const initialFilterValues = Object.fromEntries(FILTER_KEYS.map((key) => [key, filterSelects.get(key)?.value || '']));
    const initialMode = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.shockMode || 'vehicle';
    const selectedCompareIds = [];
    let catalog = [];
    let filteredCatalog = [];
    let visibleCount = pageSize;
    let catalogLoading = true;
    let failedPages = 0;
    let lastCompareTrigger = null;
    let searchTimer = null;
    let syncingVehicleContext = false;
    const state = {
      mode: initialMode,
      filters: { ...initialFilterValues },
      query: '',
      sort: 'relevance'
    };
    const rememberShockReturn = () => {
      try {
        sessionStorage.setItem('avid:storefront-return', JSON.stringify({
          family: 'shock',
          url: `${window.location.pathname}${window.location.search}`,
          expiresAt: Date.now() + (30 * 60 * 1000)
        }));
      } catch (_error) {
        // Route continuity is an enhancement; product navigation must still work.
      }
    };

    const initialScript = root.querySelector('[data-shock-catalog-page]');
    if (!initialScript || !form || !resultsGrid) {
      if (resultsRegion) resultsRegion.setAttribute('aria-busy', 'false');
      if (dataStatus) dataStatus.textContent = 'The catalog finder could not start. Browse the ADS collection or contact AVID.';
      return;
    }
    const initialProducts = parseCatalogScript(initialScript);
    const initialPage = Number(initialScript.dataset.currentPage || 1);
    const renderedCards = new Map();

    function setVehicleDirectoryLoading(loading) {
      ['make', 'model', 'subModel', 'years'].forEach((key) => {
        const select = filterSelects.get(key);
        if (!select) return;
        select.disabled = loading || (key === 'subModel' && (!state.filters.model || select.options.length === 1));
        select.setAttribute('aria-busy', String(loading));
      });
    }

    function parseCatalogScript(script) {
      try {
        const parsed = JSON.parse(script.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (_error) {
        return [];
      }
    }

    function mergeProducts(rawProducts) {
      const existing = new Map(catalog.map((product) => [product.id, product]));
      rawProducts.forEach((raw, offset) => {
        const normalized = normalizeProduct(raw, existing.size + offset, reviewMode);
        if (!existing.has(normalized.id)) existing.set(normalized.id, normalized);
      });
      catalog = [...existing.values()];
    }

    function replaceProducts(rawProducts) {
      const next = new Map();
      rawProducts.forEach((raw, index) => {
        const normalized = normalizeProduct(raw, index, reviewMode);
        if (!next.has(normalized.id)) next.set(normalized.id, normalized);
      });
      catalog = [...next.values()];
    }

    function setMode(mode, options = {}) {
      const safeMode = MODE_FILTERS[mode] ? mode : 'vehicle';
      state.mode = safeMode;
      tabs.forEach((tab) => {
        const selected = tab.dataset.shockMode === safeMode;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.shockPanel !== safeMode;
      });
      if (options.refreshOptions !== false) populateAllOptions();
      if (options.focusTab) tabs.find((tab) => tab.dataset.shockMode === safeMode)?.focus();
      if (options.apply !== false) applyState();
    }

    function getOptionEntries(key, source = catalog) {
      const entries = new Map();
      const add = (value, label = value, numeric = null) => {
        const normalizedValue = normalizeSpace(value);
        if (!normalizedValue || entries.has(normalizedValue)) return;
        entries.set(normalizedValue, { value: normalizedValue, label: normalizeSpace(label), numeric });
      };
      source.forEach((product) => {
        if (key === 'make' && product.exact.has('make')) product.makes.forEach((value) => add(value));
        if (key === 'model' && product.exact.has('model')) product.models.forEach((value) => add(value));
        if (key === 'years' && product.exact.has('years')) product.years.forEach((value) => add(value));
        if (key === 'position' && product.exact.has('position')) product.positions.forEach((value) => add(value, humanize(value)));
        if (key === 'trackWidth' && product.exact.has('trackWidth') && product.trackWidth !== null) add(formatNumber(product.trackWidth), formatInches(product.trackWidth), product.trackWidth);
        if (key === 'type' && product.exact.has('type') && product.type) add(product.type, TYPE_LABELS[product.type] || humanize(product.type));
        if (key === 'body' && product.exact.has('body') && product.bodyNumber !== null) add(formatNumber(product.bodyNumber), formatInches(product.bodyNumber), product.bodyNumber);
        if (key === 'stroke' && product.exact.has('stroke') && product.strokeNumber !== null) add(formatNumber(product.strokeNumber), formatInches(product.strokeNumber), product.strokeNumber);
        if (key === 'collapsed' && product.exact.has('collapsed') && product.collapsedNumber !== null) add(formatNumber(product.collapsedNumber), formatInches(product.collapsedNumber), product.collapsedNumber);
        if (key === 'extended' && product.exact.has('extended') && product.extendedNumber !== null) add(formatNumber(product.extendedNumber), formatInches(product.extendedNumber), product.extendedNumber);
        if (key === 'mountBolt' && product.exact.has('mountBolt') && product.mountBolt) add(product.mountBolt, formatInches(product.mountBolt));
        if (key === 'reservoir' && product.exact.has('reservoir') && product.reservoir) add(product.reservoir, humanize(product.reservoir));
        if (key === 'bypassTubes' && product.exact.has('bypassTubes') && product.bypassTubeCount !== null) add(String(product.bypassTubeCount), `${product.bypassTubeCount} tube${product.bypassTubeCount === 1 ? '' : 's'}`, product.bypassTubeCount);
        if (key === 'manufacturer' && product.exact.has('manufacturer') && product.manufacturer) add(product.manufacturer);
        if (key === 'adjustment' && filterMatches(product, key, 'compression')) add('compression', 'Compression adjustable');
        if (key === 'adjustment' && filterMatches(product, key, 'rebound')) add('rebound', 'Rebound adjustable');
        if (key === 'availability' && product.available) add('available', 'Available now');
      });
      const values = [...entries.values()];
      const numericKey = ['trackWidth', 'body', 'stroke', 'collapsed', 'extended', 'bypassTubes'].includes(key);
      return values.sort((left, right) => numericKey
        ? (left.numeric ?? Number.POSITIVE_INFINITY) - (right.numeric ?? Number.POSITIVE_INFINITY)
        : left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: 'base' }));
    }

    function getReviewedVehicleOptionEntries(key) {
      const fitments = productsForDependentFilter(key).flatMap((product) => product.vehicleFitments)
        .filter((fitment) => reviewedVehicleFitmentMatches(fitment, key));
      const entries = new Map();
      const add = (value, label = value, numeric = null) => {
        const normalizedValue = normalizeSpace(value);
        if (!normalizedValue || entries.has(normalizedValue)) return;
        entries.set(normalizedValue, { value: normalizedValue, label: normalizeSpace(label), numeric });
      };
      fitments.forEach((fitment) => {
        if (key === 'make') add(fitment.make);
        if (key === 'model') add(fitment.modelId,fitment.model);
        if (key === 'subModel' && fitment.subModel) add(fitment.submodelId,fitment.subModel);
        if (key === 'years') fitment.years.forEach((year) => add(year, year, Number(year)));
        if (key === 'position') add(fitment.position, humanize(fitment.position));
        if (key === 'trackWidth' && fitment.trackWidth !== null) add(formatNumber(fitment.trackWidth), formatInches(fitment.trackWidth), fitment.trackWidth);
      });
      const values = [...entries.values()];
      return values.sort((left, right) => ['years', 'trackWidth'].includes(key)
        ? (left.numeric ?? Number.POSITIVE_INFINITY) - (right.numeric ?? Number.POSITIVE_INFINITY)
        : left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: 'base' }));
    }

    function productsForDependentFilter(key) {
      // Ignore this dropdown's selection so customers can switch to another
      // value, but require every other active condition to have a match.
      return getFilteredCatalog(key);
    }

    function populateSelect(key, preserveValue = true) {
      const select = filterSelects.get(key);
      if (!select || !DYNAMIC_FILTERS.includes(key)) return;
      const selected = preserveValue ? state.filters[key] : '';
      const fragment = document.createDocumentFragment();
      const placeholder = createElement('option', '', OPTION_PLACEHOLDERS[key] || `All ${FILTER_LABELS[key] || key}`);
      placeholder.value = '';
      fragment.append(placeholder);
      const vehicleKey = ['make', 'model', 'subModel', 'years', 'position', 'trackWidth'].includes(key);
      const options = state.mode === 'vehicle' && vehicleKey
        ? getReviewedVehicleOptionEntries(key)
        : getOptionEntries(key, productsForDependentFilter(key));
      options.forEach((entry) => {
        const option = createElement('option', '', entry.label);
        option.value = entry.value;
        fragment.append(option);
      });
      select.replaceChildren(fragment);
      const hasSelected = [...select.options].some((option) => option.value === selected);
      select.value = hasSelected ? selected : '';
      if (!hasSelected && selected && !catalogLoading) state.filters[key] = '';
      if (key === 'subModel') select.disabled = !state.filters.model || select.options.length === 1;
      if (key === 'model') select.disabled = Boolean(state.filters.make) && select.options.length === 1;
    }

    function populateAllOptions() {
      const activeKeys = [...MODE_FILTERS[state.mode], ...COMMON_FILTERS];
      // A saved URL can contain an obsolete combination. Rebuild after clearing
      // unsupported values so earlier dropdowns also reflect the recovered state.
      for (let pass = 0; pass <= activeKeys.length; pass += 1) {
        const previous = JSON.stringify(state.filters);
        activeKeys.forEach((key) => populateSelect(key));
        if (previous === JSON.stringify(state.filters)) break;
      }
    }

    function syncControls() {
      filterSelects.forEach((select, key) => {
        const desired = state.filters[key] || '';
        select.value = [...select.options].some((option) => option.value === desired) ? desired : '';
      });
      if (partSearch) partSearch.value = state.query;
      if (sortSelect) sortSelect.value = state.sort;
      setMode(state.mode, { apply: false });
    }

    function readUrlState({ useInitialDefaults = false } = {}) {
      const params = new URL(window.location.href).searchParams;
      const hasShockState = params.has(`${QUERY_PREFIX}mode`)
        || params.has(`${QUERY_PREFIX}q`)
        || FILTER_KEYS.some((key) => params.has(`${QUERY_PREFIX}${key}`));
      state.filters = Object.fromEntries(FILTER_KEYS.map((key) => [key, useInitialDefaults && !hasShockState ? initialFilterValues[key] : '']));
      state.mode = MODE_FILTERS[params.get(`${QUERY_PREFIX}mode`)] ? params.get(`${QUERY_PREFIX}mode`) : initialMode;
      state.query = params.get(`${QUERY_PREFIX}q`) || '';
      state.sort = ['relevance', 'title', 'price-asc', 'price-desc', 'stroke'].includes(params.get(`${QUERY_PREFIX}sort`))
        ? params.get(`${QUERY_PREFIX}sort`)
        : 'relevance';
      FILTER_KEYS.forEach((key) => {
        if (params.has(`${QUERY_PREFIX}${key}`)) state.filters[key] = params.get(`${QUERY_PREFIX}${key}`) || '';
      });
      const vehicle = !hasShockState && globalThis.AvidVehicleSelector?.current();
      if (vehicle) Object.assign(state.filters,{make:vehicle.make,model:vehicle.modelId,subModel:vehicle.submodelId || '',years:String(vehicle.year)});
    }

    function updateUrl() {
      try {
        const url = new URL(window.location.href);
        [...url.searchParams.keys()].forEach((key) => {
          if (key.startsWith(QUERY_PREFIX)) url.searchParams.delete(key);
        });
        url.searchParams.set(`${QUERY_PREFIX}mode`, state.mode);
        const activeKeys = [...MODE_FILTERS[state.mode], ...COMMON_FILTERS];
        activeKeys.forEach((key) => {
          if (state.filters[key]) url.searchParams.set(`${QUERY_PREFIX}${key}`, state.filters[key]);
        });
        if (state.mode === 'part' && state.query) url.searchParams.set(`${QUERY_PREFIX}q`, state.query);
        if (state.sort !== 'relevance') url.searchParams.set(`${QUERY_PREFIX}sort`, state.sort);
        window.history.replaceState({ shockCenter: true }, '', `${url.pathname}${url.search}`);
      } catch (_error) {
        // URL state is an enhancement; finder behavior remains available if history is restricted.
      }
    }

    function valueMatches(values, expected) {
      const target = normalizeKey(expected);
      return toArray(values).some((value) => normalizeKey(value) === target);
    }

    function numberMatches(value, expected) {
      const target = Number(expected);
      return Number.isFinite(value) && Number.isFinite(target) && Math.abs(value - target) < 0.001;
    }

    function filterMatches(product, key, expected) {
      if (!expected) return true;
      if (key === 'make') return !product.inferred.has('make') && valueMatches(product.makes, expected);
      if (key === 'model') return !product.inferred.has('model') && valueMatches(product.models, expected);
      if (key === 'years') return !product.inferred.has('years') && valueMatches(product.years, expected);
      if (key === 'position') return !product.inferred.has('position') && valueMatches(product.positions, expected);
      if (key === 'trackWidth') return !product.inferred.has('trackWidth') && numberMatches(product.trackWidth, expected);
      if (key === 'fitmentType') {
        if (product.inferred.has('fitmentType')) return false;
        if (expected === 'direct-fit') return ['direct-fit', 'verified-direct-fit', 'conditional-direct-fit'].includes(product.fitmentType);
        if (expected === 'universal') return ['universal', 'universal-custom'].includes(product.fitmentType);
        return product.fitmentType === expected;
      }
      if (key === 'type') return product.exact.has('type') && product.type === expected;
      if (key === 'body') return product.exact.has('body') && numberMatches(product.bodyNumber, expected);
      if (key === 'stroke') return product.exact.has('stroke') && numberMatches(product.strokeNumber, expected);
      if (key === 'collapsed') return product.exact.has('collapsed') && numberMatches(product.collapsedNumber, expected);
      if (key === 'extended') return product.exact.has('extended') && numberMatches(product.extendedNumber, expected);
      if (key === 'mountBolt') return product.exact.has('mountBolt') && normalizeKey(product.mountBolt) === normalizeKey(expected);
      if (key === 'reservoir') return product.exact.has('reservoir') && product.reservoir === expected;
      if (key === 'adjustment') {
        if (expected === 'compression') return product.exact.has('compressionAdjustable') && product.compressionAdjustable === true;
        if (expected === 'rebound') return product.exact.has('reboundAdjustable') && product.reboundAdjustable === true;
        if (expected === 'none') return product.exact.has('compressionAdjustable') && product.exact.has('reboundAdjustable') && product.compressionAdjustable === false && product.reboundAdjustable === false;
      }
      if (key === 'bypassTubes') return product.exact.has('bypassTubes') && Number(product.bypassTubeCount) === Number(expected);
      if (key === 'manufacturer') return product.exact.has('manufacturer') && normalizeKey(product.manufacturer) === normalizeKey(expected);
      if (key === 'availability') return expected !== 'available' || product.available;
      return true;
    }

    function reviewedVehicleFitmentMatches(fitment, ignoredKey) {
      if(fitment.pendingDiscovery)return (ignoredKey==='make'||!state.filters.make||normalizeKey(fitment.make)===normalizeKey(state.filters.make))
        && (['make','model'].includes(ignoredKey)||!state.filters.model||fitment.modelId===state.filters.model);
      return (ignoredKey === 'make' || !state.filters.make || normalizeKey(fitment.make) === normalizeKey(state.filters.make))
        && (['make', 'model'].includes(ignoredKey) || !state.filters.model || fitment.modelId === state.filters.model)
        && (['make', 'model', 'subModel'].includes(ignoredKey) || !state.filters.subModel || fitment.submodelId === '*' || fitment.submodelId === state.filters.subModel)
        && (['make', 'model', 'years'].includes(ignoredKey) || !state.filters.years || fitment.years.includes(state.filters.years))
        && (ignoredKey === 'position' || !state.filters.position || normalizeKey(fitment.position) === normalizeKey(state.filters.position))
        && (ignoredKey === 'trackWidth' || !state.filters.trackWidth || numberMatches(fitment.trackWidth, state.filters.trackWidth));
    }

    function reviewedVehicleFiltersMatch(product, ignoredKey) {
      return product.vehicleFitments.some((fitment) => reviewedVehicleFitmentMatches(fitment, ignoredKey));
    }

    function getFilteredCatalog(ignoredKey) {
      const activeKeys = [...MODE_FILTERS[state.mode], ...COMMON_FILTERS].filter((key) => key !== ignoredKey);
      let source = state.mode === 'vehicle'
        ? catalog.filter((product) => product.vehicleBrowseEligible)
        : state.mode === 'spec'
          ? catalog.filter((product) => product.specBrowseEligible)
          : catalog;
      if (state.mode === 'part' && !normalizePart(state.query)) return [];
      const vehicleKeys = new Set(['make', 'model', 'subModel', 'years', 'position', 'trackWidth']);
      let matches = source.filter((product) => (
        (state.mode !== 'vehicle' || reviewedVehicleFiltersMatch(product, ignoredKey))
        && (state.mode !== 'vehicle' || ignoredKey || product.fitmentType !== 'incompatible')
        && activeKeys.filter((key) => state.mode !== 'vehicle' || !vehicleKeys.has(key)).every((key) => filterMatches(product, key, state.filters[key]))
      ));
      if (state.mode === 'part') {
        const query = normalizePart(state.query);
        matches = matches.filter((product) => {
          if (product.browseEligible) return product.partSearchText.includes(query);
          return normalizePart(product.sku) === query || normalizePart(product.manufacturerPartNumber) === query;
        });
      }
      const score = (product) => {
        let value = product.available ? 3 : 0;
        if (product.dataStatus === 'verified') value += 4;
        if (product.dataStatus === 'partial') value += 2;
        if (state.mode === 'part' && state.query) {
          const query = normalizePart(state.query);
          if (normalizePart(product.sku) === query) value += 100;
          else if (normalizePart(product.manufacturerPartNumber) === query) value += 95;
          else if (normalizePart(product.sku).startsWith(query)) value += 75;
          else if (product.partSearchText.startsWith(query)) value += 55;
        }
        if (['verified-direct-fit', 'direct-fit'].includes(product.fitmentType)) value += 2;
        return value;
      };
      matches.sort((left, right) => {
        if (state.sort === 'title') return left.title.localeCompare(right.title, undefined, { numeric: true, sensitivity: 'base' });
        if (state.sort === 'price-asc') return left.price - right.price || left.rank - right.rank;
        if (state.sort === 'price-desc') return right.price - left.price || left.rank - right.rank;
        if (state.sort === 'stroke') return (left.strokeNumber ?? Number.POSITIVE_INFINITY) - (right.strokeNumber ?? Number.POSITIVE_INFINITY) || left.rank - right.rank;
        return score(right) - score(left) || left.rank - right.rank;
      });
      return matches;
    }

    function statusBadge(product) {
      const labels = {
        verified: 'Complete specifications',
        partial: 'Partial specs',
        blocked: 'AVID review required',
        missing: 'Data review pending'
      };
      const badge = createElement('span', `shock-product-card__data-status shock-product-card__data-status--${product.dataStatus}`, labels[product.dataStatus] || 'Data review pending');
      return badge;
    }

    function specValue(product, key, value, formatter = normalizeSpace) {
      if (!isPresent(value) || !product.exact.has(key)) return null;
      const item = createElement('div', 'shock-product-card__spec');
      item.append(createElement('span', '', FILTER_LABELS[key] || humanize(key)));
      const strong = createElement('strong', '', formatter(value));
      item.append(strong);
      return item;
    }

    function createProductCard(product, index) {
      const restricted = state.mode === 'part' && !product.browseEligible;
      const card = createElement('article', `shock-product-card${restricted ? ' shock-product-card--review-only' : ''}`);
      card.dataset.productId = product.id;

      const mediaLink = createElement(restricted ? 'div' : 'a', 'shock-product-card__media');
      if (!restricted) {
        mediaLink.href = product.url;
        mediaLink.setAttribute('aria-label', `View ${product.title}`);
      }
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.srcset = responsiveSrcset(product.image, [240, 360, 540, 720]);
        image.sizes = '(max-width: 749px) 92vw, (max-width: 1199px) 46vw, 31vw';
        image.alt = product.imageAlt;
        image.loading = index < 3 ? 'eager' : 'lazy';
        image.fetchPriority = index === 0 ? 'high' : 'auto';
        image.decoding = 'async';
        mediaLink.append(image);
      } else {
        mediaLink.append(createElement('span', 'shock-product-card__placeholder', 'ADS'));
      }
      const badges = createElement('div', 'shock-product-card__badges');
      if (product.exact.has('type') && product.type) badges.append(createElement('span', '', TYPE_LABELS[product.type] || humanize(product.type)));
      if (!restricted && !product.inferred.has('fitmentType') && ['direct-fit', 'verified-direct-fit', 'conditional-direct-fit'].includes(product.fitmentType)) {
        badges.append(createElement('span', 'is-fitment', FITMENT_LABELS[product.fitmentType] || 'Direct fit'));
      }
      if (restricted) badges.append(createElement('span', 'is-review', 'Review only'));
      if(product.fitmentType==='needs-review')badges.append(createElement('span','is-review','Needs fitment review'));
      if(product.fitmentType==='incompatible')badges.append(createElement('span','is-review','Not compatible with your selected vehicle'));
      mediaLink.append(badges);
      card.append(mediaLink);

      const body = createElement('div', 'shock-product-card__body');
      const maker = createElement('p', 'shock-product-card__maker');
      maker.append(createElement('span', '', product.manufacturer || product.vendor || 'Shock product'));
      if (product.sku) maker.append(createElement('span', '', product.sku));
      body.append(maker);
      const title = createElement('h3');
      if (restricted) {
        title.textContent = product.title;
      } else {
        const titleLink = createElement('a', '', product.title);
        titleLink.href = product.url;
        title.append(titleLink);
      }
      body.append(title);
      if (restricted) {
        const warning = createElement('div', 'shock-product-card__review-warning');
        warning.setAttribute('role', 'note');
        warning.append(createElement('strong', '', 'Not approved for online selection'));
        warning.append(createElement('p', '', 'This exact part-number record is blocked, incomplete, missing final eligibility, or has no approved selling price. Its product link and purchase cues are disabled until AVID reviews it.'));
        body.append(warning);
      }

      const specs = createElement('div', 'shock-product-card__specs');
      const candidates = [
        specValue(product, 'body', product.bodyNumber, formatInches),
        specValue(product, 'stroke', product.strokeNumber, formatInches),
        specValue(product, 'collapsed', product.collapsedNumber, formatInches),
        specValue(product, 'extended', product.extendedNumber, formatInches),
        specValue(product, 'position', product.positions.slice(0, 2).join(' / ')),
        specValue(product, 'reservoir', product.reservoir, humanize)
      ].filter(Boolean).slice(0, 4);
      if (candidates.length) specs.append(...candidates);
      else specs.append(createElement('p', 'shock-product-card__specs-pending', 'Detailed specifications are not yet available here. Open the product page or contact AVID for confirmation.'));
      body.append(specs);

      const footer = createElement('div', 'shock-product-card__footer');
      const priceBlock = createElement('div', 'shock-product-card__price');
      if (restricted) priceBlock.append(createElement('strong', '', 'Price withheld pending review'));
      else {
        const price = globalThis.AvidDealerPricing?.createPriceElement?.({
          variantIds: product.variantId,
          priceText: product.priceLabel || 'See product',
          priceCents: product.price,
          compareAtPriceCents: product.compareAtPrice,
          compareAtPriceText: product.compareAtPriceLabel
        });
        priceBlock.append(price || createElement('strong', '', product.priceLabel || 'See product'));
      }
      priceBlock.append(statusBadge(product));
      footer.append(priceBlock);
      const actions = createElement('div', 'shock-product-card__actions');
      if (restricted) {
        const reviewLink = createElement('a', 'shock-product-card__details shock-product-card__review-link', 'Request AVID review');
        reviewLink.href = supportUrl;
        actions.append(reviewLink);
      } else {
        const compareButton = createElement('button', 'shock-product-card__compare', 'Compare');
        compareButton.type = 'button';
        compareButton.dataset.shockCompareId = product.id;
        compareButton.setAttribute('aria-pressed', 'false');
        actions.append(compareButton);
        const detailsLink = createElement('a', 'shock-product-card__details', 'View details');
        detailsLink.href = product.url;
        actions.append(detailsLink);
      }
      footer.append(actions);
      body.append(footer);
      card.append(body);
      return card;
    }

    function displayFilterValue(key, value) {
      const select = filterSelects.get(key);
      const option = select ? [...select.options].find((item) => item.value === value) : null;
      if (option) return option.textContent;
      if (key === 'type') return TYPE_LABELS[value] || humanize(value);
      if (key === 'fitmentType') return FITMENT_LABELS[value] || humanize(value);
      return value;
    }

    function renderChips() {
      chips.replaceChildren();
      const fragment = document.createDocumentFragment();
      const activeKeys = [...MODE_FILTERS[state.mode], ...COMMON_FILTERS];
      activeKeys.forEach((key) => {
        const value = state.filters[key];
        if (!value) return;
        const button = createElement('button', 'shock-results__chip');
        button.type = 'button';
        button.dataset.shockRemoveFilter = key;
        button.setAttribute('aria-label', `Remove ${FILTER_LABELS[key]} filter: ${displayFilterValue(key, value)}`);
        button.append(createElement('span', '', `${FILTER_LABELS[key]}: ${displayFilterValue(key, value)}`));
        button.append(createElement('span', '', '×'));
        fragment.append(button);
      });
      if (state.mode === 'part' && state.query) {
        const button = createElement('button', 'shock-results__chip');
        button.type = 'button';
        button.dataset.shockRemoveFilter = 'query';
        button.setAttribute('aria-label', `Clear part-number search: ${state.query}`);
        button.append(createElement('span', '', `Part number: ${state.query}`));
        button.append(createElement('span', '', '×'));
        fragment.append(button);
      }
      chips.append(fragment);
    }

    function renderResults() {
      const selector = globalThis.AvidVehicleSelector;
      const previous = selector?.current();
      const context = state.mode === 'vehicle' ? resolveShockVehicle(state.filters,selector?.directory() || [],previous) : previous;
      if (state.mode === 'vehicle') {
        syncingVehicleContext = true;
        try {
          if (context && (context.applicationId !== previous?.applicationId || context.year !== previous?.year)) selector.select(context);
          else if (!context && previous) selector.clear();
        } finally { syncingVehicleContext = false; }
      }
      catalog.forEach(product => {
        product.fitmentType = contextualFitmentType(product,context);
        product.url = context ? selector.destination({url:product.sourceUrl},context)
          : selector?.withNavigationContext(product.sourceUrl).toString() || product.sourceUrl;
      });
      filteredCatalog = getFilteredCatalog();
      const shown = Math.min(visibleCount, filteredCatalog.length);
      const awaitingPartQuery = state.mode === 'part' && !normalizePart(state.query);
      const visibleProducts = filteredCatalog.slice(0, shown);
      const visibleIds = new Set(visibleProducts.map((product) => product.id));
      for (const [id, entry] of renderedCards) {
        if (!visibleIds.has(id)) { entry.card.remove(); renderedCards.delete(id); }
      }
      visibleProducts.forEach((product, index) => {
        // Keep unchanged images, dealer price nodes and keyboard focus in place
        // when background catalog pages finish. Ranking still uses the full catalog.
        const signature = JSON.stringify([state.mode, product], (key, value) => key === 'rank' ? undefined : value instanceof Set ? [...value] : value);
        let entry = renderedCards.get(product.id);
        if (!entry || entry.signature !== signature) {
          const card = createProductCard(product, index);
          entry?.card.replaceWith(card);
          entry = { card, signature };
          renderedCards.set(product.id, entry);
        }
        const current = resultsGrid.children[index];
        if (current !== entry.card) resultsGrid.insertBefore(entry.card, current || null);
        const image = entry.card.querySelector('img');
        if (image) {
          image.loading = index < 3 ? 'eager' : 'lazy';
          image.fetchPriority = index === 0 ? 'high' : 'auto';
        }
      });
      resultsGrid.dataset.initialResultsReady = 'true';
      const noun = filteredCatalog.length === 1 ? 'shock' : 'shocks';
      resultCount.textContent = awaitingPartQuery
        ? 'Enter an exact part number or SKU to search the complete catalog'
        : filteredCatalog.length
          ? `Showing ${shown} of ${filteredCatalog.length} matching ${noun}`
          : 'No shocks match every selected condition';
      emptyState.hidden = awaitingPartQuery || filteredCatalog.length !== 0;
      resultsGrid.hidden = awaitingPartQuery || filteredCatalog.length === 0;
      loadMore.hidden = awaitingPartQuery || shown >= filteredCatalog.length;
      renderChips();
      updateCompareUI();
    }

    function applyState({ updateHistory = true, resetVisible = true } = {}) {
      if (resetVisible) visibleCount = pageSize;
      syncControls();
      renderResults();
      if (updateHistory) updateUrl();
    }

    function clearAllFilters() {
      if (vehicleContextRegion) vehicleContextRegion.hidden = true;
      FILTER_KEYS.forEach((key) => { state.filters[key] = ''; });
      state.query = '';
      state.sort = 'relevance';
      populateAllOptions();
      applyState();
    }

    function updateCompareButtons() {
      const atLimit = selectedCompareIds.length >= 3;
      root.querySelectorAll('[data-shock-compare-id]').forEach((button) => {
        const selected = selectedCompareIds.includes(button.dataset.shockCompareId);
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-selected', selected);
        button.textContent = selected ? 'Selected' : 'Compare';
        button.disabled = atLimit && !selected;
        button.title = atLimit && !selected ? 'Remove a selected shock before adding another.' : '';
      });
    }

    function updateCompareUI() {
      compareCount.textContent = String(selectedCompareIds.length);
      compareBar.hidden = selectedCompareIds.length === 0;
      document.documentElement.toggleAttribute('data-shock-compare-active', selectedCompareIds.length > 0);
      updateCompareButtons();
    }

    function toggleCompare(productId, trigger) {
      const index = selectedCompareIds.indexOf(productId);
      if (index >= 0) {
        selectedCompareIds.splice(index, 1);
      } else if (selectedCompareIds.length < 3) {
        selectedCompareIds.push(productId);
      } else {
        resultCount.textContent = 'Comparison is limited to three shocks. Remove one before adding another.';
        trigger?.focus();
        return;
      }
      updateCompareUI();
      if (compareDialog?.open) renderCompare();
    }

    function comparisonValue(product, key) {
      const published = (field, value) => product.exact.has(field) ? value : '';
      const adjustment = product.exact.has('compressionAdjustable') && product.exact.has('reboundAdjustable')
        ? product.compressionAdjustable === true && product.reboundAdjustable === true
          ? 'Compression + rebound'
          : product.compressionAdjustable === true
            ? 'Compression'
            : product.reboundAdjustable === true
              ? 'Rebound'
              : product.compressionAdjustable === false && product.reboundAdjustable === false
                ? 'None listed'
                : ''
        : product.exact.has('compressionAdjustable') && product.compressionAdjustable === true
          ? 'Compression'
          : product.exact.has('reboundAdjustable') && product.reboundAdjustable === true
            ? 'Rebound'
            : '';
      const values = {
        price: product.priceLabel,
        sku: product.sku || product.manufacturerPartNumber,
        type: published('type', TYPE_LABELS[product.type] || humanize(product.type)),
        fitment: published('fitmentType', FITMENT_LABELS[product.fitmentType] || humanize(product.fitmentType)),
        application: published('application', product.application) || published('model', product.models.join(', ')),
        position: published('position', product.positions.join(' / ')),
        body: product.bodyNumber !== null ? published('body', formatInches(product.bodyNumber)) : '',
        shaft: product.shaftNumber !== null ? published('shaft', formatInches(product.shaftNumber)) : '',
        stroke: product.strokeNumber !== null ? published('stroke', formatInches(product.strokeNumber)) : '',
        collapsed: product.collapsedNumber !== null ? published('collapsed', formatInches(product.collapsedNumber)) : '',
        extended: product.extendedNumber !== null ? published('extended', formatInches(product.extendedNumber)) : '',
        mountingWidth: product.mountingWidth ? published('mountingWidth', formatInches(product.mountingWidth)) : '',
        mountBolt: product.mountBolt ? published('mountBolt', formatInches(product.mountBolt)) : '',
        reservoir: published('reservoir', humanize(product.reservoir)),
        adjustment,
        bypassTubes: product.bypassTubeCount !== null ? published('bypassTubes', `${product.bypassTubeCount} tube${product.bypassTubeCount === 1 ? '' : 's'}`) : '',
        unit: published('unitOfSale', product.unitOfSale),
        status: product.dataStatus === 'verified' ? 'Complete' : product.dataStatus === 'partial' ? 'Partial' : 'Contact AVID'
      };
      return values[key] || 'Not available';
    }

    function renderCompare() {
      const products = selectedCompareIds.map((id) => catalog.find((product) => product.id === id)).filter(Boolean);
      compareContent.replaceChildren();
      if (!products.length) {
        compareContent.append(createElement('p', 'shock-compare__empty', 'Select up to three products from the finder to compare their specifications.'));
        return;
      }
      const selected = createElement('div', 'shock-compare__selected');
      products.forEach((product) => {
        const item = createElement('article');
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.srcset = responsiveSrcset(product.image, [120, 180, 240]);
          image.sizes = '88px';
          image.alt = '';
          image.loading = 'lazy';
          item.append(image);
        }
        const link = createElement('a', '', product.title);
        link.href = product.url;
        item.append(link);
        const remove = createElement('button', '', 'Remove');
        remove.type = 'button';
        remove.dataset.shockCompareRemove = product.id;
        remove.setAttribute('aria-label', `Remove ${product.title} from comparison`);
        item.append(remove);
        selected.append(item);
      });
      compareContent.append(selected);

      const rows = [
        ['price', 'Price'],
        ['sku', 'Part number / SKU'],
        ['type', 'Shock type'],
        ['fitment', 'Fitment class'],
        ['application', 'Application'],
        ['position', 'Position'],
        ['body', 'Body diameter'],
        ['shaft', 'Shaft diameter'],
        ['stroke', 'Stroke'],
        ['collapsed', 'Collapsed length'],
        ['extended', 'Extended length'],
        ['mountingWidth', 'Mounting width'],
        ['mountBolt', 'Mount bolt size'],
        ['reservoir', 'Reservoir'],
        ['adjustment', 'External adjustment'],
        ['bypassTubes', 'Bypass tubes'],
        ['unit', 'Unit of sale'],
        ['status', 'Data status']
      ];
      const tableWrap = createElement('div', 'shock-compare__table-wrap');
      const table = createElement('table');
      const caption = createElement('caption', 'visually-hidden', 'Specifications for selected shocks');
      table.append(caption);
      const head = createElement('thead');
      const headRow = createElement('tr');
      headRow.append(createElement('th', '', 'Specification'));
      products.forEach((product) => headRow.append(createElement('th', '', product.sku || product.manufacturerPartNumber || product.title)));
      head.append(headRow);
      table.append(head);
      const body = createElement('tbody');
      rows.forEach(([key, label]) => {
        const row = createElement('tr');
        const labelCell = createElement('th', '', label);
        labelCell.scope = 'row';
        row.append(labelCell);
        products.forEach((product) => {
          const cell = createElement('td');
          if (key === 'price' && product.browseEligible) {
            const price = globalThis.AvidDealerPricing?.createPriceElement?.({
              variantIds: product.variantId,
              priceText: comparisonValue(product, key),
              priceCents: product.price,
              compareAtPriceCents: product.compareAtPrice,
              compareAtPriceText: product.compareAtPriceLabel
            });
            if (price) cell.append(price);
            else cell.textContent = comparisonValue(product, key);
          } else cell.textContent = comparisonValue(product, key);
          row.append(cell);
        });
        body.append(row);
      });
      table.append(body);
      tableWrap.append(table);
      compareContent.append(tableWrap);
    }

    async function fetchCatalogPage(page) {
      const url = new URL(window.location.href);
      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith(QUERY_PREFIX)) url.searchParams.delete(key);
      });
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));
      url.hash = '';
      let lastError;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: controller.signal
          });
          if (!response.ok) throw new Error(`Catalog page ${page} returned ${response.status}`);
          const html = await response.text();
          const documentPage = new DOMParser().parseFromString(html, 'text/html');
          const script = documentPage.querySelector('[data-shock-catalog-page]');
          if (!script) throw new Error(`Catalog page ${page} did not include product data`);
          return parseCatalogScript(script);
        } catch (error) {
          lastError = error;
          if (attempt === 0) await new Promise((resolve) => window.setTimeout(resolve, 350));
        } finally {
          window.clearTimeout(timeout);
        }
      }
      throw lastError;
    }

    function completeCatalogStatus() {
      const browseCount = catalog.filter((product) => product.browseEligible).length;
      const heldCount = catalog.length - browseCount;
      const qualifier = reviewMode ? 'products available' : 'products available';
      return `${browseCount} ${qualifier}${heldCount ? ` · ${heldCount} records held for exact part-number review` : ''}.`;
    }

    async function loadCompleteCatalog(totalPages, reuseInitialPage = false) {
      failedPages = 0;
      catalogLoading = true;
      setVehicleDirectoryLoading(true);
      resultsRegion.setAttribute('aria-busy', 'true');
      if (loadCatalog) {
        loadCatalog.disabled = true;
        loadCatalog.textContent = 'Loading complete catalog...';
      }
      const pages = Array.from({ length: totalPages }, (_value, index) => index + 1);
      const expectedInitialCount = Math.min(50, sourceProductCount - (initialPage - 1) * 50);
      const canReuseInitial = reuseInitialPage && pages.includes(initialPage) && expectedInitialCount > 0 && initialProducts.length === expectedInitialCount;
      const pendingPages = pages.filter((page) => !canReuseInitial || page !== initialPage);
      let cursor = 0;
      let completed = canReuseInitial ? 1 : 0;
      const loadedByPage = new Map();
      if (canReuseInitial) loadedByPage.set(initialPage, initialProducts);
      const worker = async () => {
        while (cursor < pendingPages.length) {
          const page = pendingPages[cursor];
          cursor += 1;
          try {
            loadedByPage.set(page, await fetchCatalogPage(page));
          } catch (_error) {
            failedPages += 1;
          }
          completed += 1;
          dataStatus.textContent = `Loading the complete shock catalog: ${completed} of ${totalPages} pages.`;
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, pendingPages.length) }, worker));
      const loaded = flattenCatalogPages(pages, loadedByPage);
      if (failedPages) mergeProducts(loaded);
      else replaceProducts(loaded);
      const sourceCountMismatch = sourceProductCount > 0 && catalog.length !== sourceProductCount;
      catalogLoading = failedPages > 0 || sourceCountMismatch;
      populateAllOptions();
      syncControls();
      renderResults();
      setVehicleDirectoryLoading(false);
      resultsRegion.setAttribute('aria-busy', 'false');
      dataStatus.textContent = failedPages
        ? `${catalog.length} products available. ${failedPages} catalog page${failedPages === 1 ? '' : 's'} could not be loaded; refresh to retry.`
        : sourceCountMismatch
          ? `Some products could not load. Retry to refresh the complete list.`
          : completeCatalogStatus();
      if (catalogGate) catalogGate.hidden = failedPages === 0 && !sourceCountMismatch;
      if (loadCatalog) {
        loadCatalog.disabled = false;
        loadCatalog.textContent = failedPages || sourceCountMismatch ? 'Retry products' : 'All products loaded';
      }
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setMode(tab.dataset.shockMode));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (['ArrowRight', 'ArrowDown'].includes(event.key)) nextIndex = (index + 1) % tabs.length;
        if (['ArrowLeft', 'ArrowUp'].includes(event.key)) nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        setMode(tabs[nextIndex].dataset.shockMode, { focusTab: true });
      });
    });

    root.querySelectorAll('[data-shock-mode-link]').forEach((link) => {
      link.addEventListener('click', () => {
        setMode(link.dataset.shockModeLink);
        window.setTimeout(() => tabs.find((tab) => tab.dataset.shockMode === state.mode)?.focus({ preventScroll: true }), 1);
      });
    });

    filterSelects.forEach((select, key) => {
      select.addEventListener('change', () => {
        if (vehicleContextRegion) vehicleContextRegion.hidden = true;
        state.filters[key] = select.value;
        if (key === 'make') {
          state.filters.model = '';
          state.filters.subModel = '';
          state.filters.years = '';
          populateSelect('model');
          populateSelect('years');
        }
        if (key === 'model') {
          state.filters.subModel = '';
          state.filters.years = '';
          populateSelect('years');
        }
        applyState();
      });
    });

    partSearch?.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        state.query = partSearch.value.trim();
        applyState();
      }, 180);
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      state.query = partSearch?.value.trim() || '';
      applyState();
    });
    form.addEventListener('reset', (event) => {
      event.preventDefault();
      clearAllFilters();
    });
    sortSelect?.addEventListener('change', () => {
      state.sort = sortSelect.value;
      applyState();
    });
    loadMore?.addEventListener('click', () => {
      visibleCount += pageSize;
      renderResults();
      const firstNewCard = resultsGrid.children[Math.max(0, visibleCount - pageSize)];
      firstNewCard?.querySelector('a')?.focus({ preventScroll: true });
    });
    loadCatalog?.addEventListener('click', () => {
      const totalPages = Math.max(1, Number(initialScript.dataset.totalPages) || 1);
      loadCompleteCatalog(totalPages);
    });
    chips?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-shock-remove-filter]');
      if (!button) return;
      const key = button.dataset.shockRemoveFilter;
      if (key === 'query') state.query = '';
      else state.filters[key] = '';
      if (key === 'make') {
        state.filters.model = '';
        state.filters.subModel = '';
        state.filters.years = '';
        populateSelect('model');
        populateSelect('years');
      }
      if (key === 'model') {
        state.filters.subModel = '';
        state.filters.years = '';
        populateSelect('years');
      }
      applyState();
    });
    resultsGrid.addEventListener('click', (event) => {
      const button = event.target.closest('[data-shock-compare-id]');
      if (button) toggleCompare(button.dataset.shockCompareId, button);
      const productLink = event.target.closest('.shock-product-card a[href*="/products/"]');
      if (productLink) rememberShockReturn();
    });

    root.querySelector('[data-shock-clear-compare]')?.addEventListener('click', () => {
      selectedCompareIds.splice(0);
      updateCompareUI();
      if (compareDialog?.open) compareDialog.close();
    });
    root.querySelector('[data-shock-open-compare]')?.addEventListener('click', (event) => {
      lastCompareTrigger = event.currentTarget;
      renderCompare();
      if (typeof compareDialog.showModal === 'function') compareDialog.showModal();
      else compareDialog.setAttribute('open', '');
      root.querySelector('[data-shock-close-compare]')?.focus();
    });
    root.querySelector('[data-shock-close-compare]')?.addEventListener('click', () => compareDialog.close());
    compareContent?.addEventListener('click', (event) => {
      if (event.target.closest('a[href*="/products/"]')) rememberShockReturn();
      const button = event.target.closest('[data-shock-compare-remove]');
      if (!button) return;
      toggleCompare(button.dataset.shockCompareRemove, button);
      if (!selectedCompareIds.length) compareDialog.close();
    });
    compareDialog?.addEventListener('click', (event) => {
      if (event.target !== compareDialog) return;
      const rect = compareDialog.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) compareDialog.close();
    });
    compareDialog?.addEventListener('close', () => lastCompareTrigger?.focus());

    window.addEventListener('popstate', () => {
      readUrlState({ useInitialDefaults: true });
      populateAllOptions();
      applyState({ updateHistory: false });
    });
    document.addEventListener('avid:vehicle-context-change', event => {
      if (syncingVehicleContext) return;
      const vehicle = event.detail?.context;
      Object.assign(state.filters,{make:vehicle?.make || '',model:vehicle?.modelId || '',subModel:vehicle?.submodelId || '',years:vehicle ? String(vehicle.year) : ''});
      populateAllOptions();
      applyState();
    });

    mergeProducts(initialProducts);
    populateAllOptions();
    readUrlState({ useInitialDefaults: true });
    populateAllOptions();
    syncControls();
    renderResults();
    resultsRegion.setAttribute('aria-busy', 'true');
    dataStatus.textContent = 'Loading the complete Shock Center catalog…';
    const totalPages = Math.max(1, Number(initialScript.dataset.totalPages) || 1);
    const requestedCatalogFilter = (() => {
      const params = new URL(window.location.href).searchParams;
      return params.has(`${QUERY_PREFIX}q`)
        || (params.has(`${QUERY_PREFIX}sort`) && params.get(`${QUERY_PREFIX}sort`) !== 'relevance')
        || FILTER_KEYS.some((key) => params.has(`${QUERY_PREFIX}${key}`));
    })();
    catalogLoading = totalPages > 1;
    resultsRegion.setAttribute('aria-busy', 'false');
    if (totalPages > 1) {
      if (catalogGate) catalogGate.hidden = true;
      dataStatus.textContent = requestedCatalogFilter
        ? 'Loading the complete Shock Center catalog for this saved search…'
        : 'Loading every available vehicle and specification option. Initial products remain available while the catalog finishes loading.';
      void loadCompleteCatalog(totalPages, true);
    } else {
      if (catalogGate) catalogGate.hidden = true;
      setVehicleDirectoryLoading(false);
      dataStatus.textContent = completeCatalogStatus();
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__AVID_SHOCK_CENTER_TEST_HOOK__) {
    Object.assign(globalThis.__AVID_SHOCK_CENTER_TEST_HOOK__, {
      normalizeVehicleFitments,
      canonicalDataStatus,
      canonicalType,
      flattenCatalogPages,
      normalizeProduct,
      resolveShockVehicle,
      contextualFitmentType,
      toFacetArray
    });
  }

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-shock-center]')) initShockCenter(scope);
    scope.querySelectorAll?.('[data-shock-center]').forEach(initShockCenter);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
